import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EyeIcon } from "lucide-react";
import { TeacherReviewPageSingleItemDto } from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "@core/index";

interface StudentsAnswersTableProps {
  studentAssignments: TeacherReviewPageSingleItemDto[];
  onStudentSelect: (studentId: number, questionId: number) => void;
}

export const StudentsAnswersTable = ({
  studentAssignments,
  onStudentSelect,
}: StudentsAnswersTableProps) => {
  const { t } = useTranslation();

  const studentStats = useMemo(() => {
    return studentAssignments.map((student) => {
      const openEndedQuestions =
        student.questionsWithStudentAnswers?.filter((q) => q.type === "OE") ||
        [];

      const totalQuestions = openEndedQuestions.length;
      const gradedQuestions = openEndedQuestions.filter(
        (q) => q.didTeacherGrade,
      ).length;

      const totalPoints = openEndedQuestions.reduce(
        (sum, q) => sum + (q.question?.point || 0),
        0,
      );
      const earnedPoints = openEndedQuestions.reduce(
        (sum, q) => sum + (q.score || 0),
        0,
      );

      const firstUngradedQuestion = openEndedQuestions.find(
        (q) => !q.didTeacherGrade,
      );

      return {
        studentId: student.studentId,
        studentName: `${student.studentName} ${student.studentSurname}`,
        progress: (gradedQuestions / totalQuestions) * 100,
        totalQuestions,
        gradedQuestions,
        totalPoints,
        earnedPoints,
        firstUngradedQuestionId:
          firstUngradedQuestion?.questionId ?? openEndedQuestions[0].questionId,
      };
    });
  }, [studentAssignments]);

  if (!studentAssignments || studentAssignments.length === 0) {
    return (
      <div className="p-4 text-center">
        <Text variant="h3">{t("assignments.openEnded.review.noStudents")}</Text>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full table-auto">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t("assignments.openEnded.review.studentName")}
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t("common.progress")}
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t("common.point")}
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
          </tr>
        </thead>
        <tbody>
          {studentStats.map((stats) => (
            <tr
              key={stats.studentId}
              className="hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                {stats.studentName}
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-48 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className="bg-primary-600 h-2.5 rounded-full"
                      style={{ width: `${stats.progress}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {stats.gradedQuestions}/{stats.totalQuestions}
                  </span>
                </div>
              </td>
              <td className="px-4 py-4 text-center">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {stats.earnedPoints}/{stats.totalPoints}
                </span>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-center gap-2">
                  <Button
                    onClick={() =>
                      onStudentSelect(
                        Number(stats.studentId),
                        Number(stats.firstUngradedQuestionId),
                      )
                    }
                    size="sm"
                    variant="solid"
                    rounded="lg"
                    className="bg-primary-500 hover:bg-primary-700 text-white"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    {t("common.continue")}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
